import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Post } from '../types';
import EbookViewer from './EbookViewer';
import AudioPlayer from './AudioPlayer';
import VideoPlayer from './VideoPlayer';
import ThreeDViewer from './ThreeDViewer';
import ImageSlider from './ImageSlider';
import { useLanguage } from '../context/LanguageContext';

// Security hook to prevent media capture
const useMediaProtection = () => {
  useEffect(() => {
    const preventContextMenu = (e: Event) => {
      e.preventDefault();
      return false;
    };

    const preventDragStart = (e: Event) => {
      e.preventDefault();
      return false;
    };

    const preventKeyboardShortcuts = (e: KeyboardEvent) => {
      // Prevent save, print, inspect, copy shortcuts
      if (
        (e.ctrlKey || e.metaKey) && 
        (e.key === 's' || e.key === 'S' || 
         e.key === 'p' || e.key === 'P' ||
         e.key === 'u' || e.key === 'U' ||
         e.key === 'c' || e.key === 'C' ||
         e.key === 'a' || e.key === 'A')
      ) {
        e.preventDefault();
        return false;
      }

      // Prevent F12, Ctrl+Shift+I, Ctrl+Shift+J
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C'))
      ) {
        e.preventDefault();
        return false;
      }
    };

    const preventSelection = () => {
      if (window.getSelection) {
        window.getSelection()?.removeAllRanges();
      }
    };

    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('dragstart', preventDragStart);
    document.addEventListener('keydown', preventKeyboardShortcuts);
    document.addEventListener('selectstart', preventSelection);

    return () => {
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('dragstart', preventDragStart);
      document.removeEventListener('keydown', preventKeyboardShortcuts);
      document.removeEventListener('selectstart', preventSelection);
    };
  }, []);
};

interface MediaViewerProps {
  post: Post;
}

const MediaViewer: React.FC<MediaViewerProps> = ({ post }) => {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const { select } = useLanguage();

  // Apply media protection
  useMediaProtection();

  const media = React.useMemo(() => {
    if (post?.media && post.media.length > 0) {
      return post.media;
    }

    const postRecord = post as Record<string, unknown>;
    const mediaFiles = postRecord?.media_files || postRecord?.mediaFiles;
    if (Array.isArray(mediaFiles) && mediaFiles.length > 0) {
      return mediaFiles;
    }
    return [];
  }, [post]);

  const currentMedia = media[currentMediaIndex];
  const images = media.filter((m) => m.type === 'image');

  useEffect(() => {
    setCurrentMediaIndex(0);
  }, [media.length, post.id]);

  const openDocumentLabel = React.useMemo(() => select({ es: 'Abrir documento', en: 'Open document' }), [select]);
  const downloadFileLabel = React.useMemo(() => select({ es: 'Descargar archivo', en: 'Download file' }), [select]);

  const renderFallbackCard = () => (
    <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900 px-6 text-center text-white">
      <div className="w-20 h-20 flex items-center justify-center rounded-full bg-purple-600 bg-opacity-80 mb-4 text-3xl">
        🖼️
      </div>
      <h3 className="text-xl font-semibold mb-2">{post.title}</h3>
      <p className="text-sm text-gray-300 max-w-md">{post.description}</p>
    </div>
  );

  if (!media.length || !currentMedia) {
    return renderFallbackCard();
  }

  const hasMultipleImages = images.length > 1;
  
  // Detect media type and render appropriate viewer
  const renderMediaViewer = () => {
    // Multiple images - use slider
    if (hasMultipleImages && currentMedia.type === 'image') {
      return <ImageSlider images={images} />;
    }
    
    // Single image
    if (currentMedia.type === 'image') {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <div className="relative">
            <img
              src={currentMedia.url}
              alt={post.title}
              className="w-full h-auto object-cover protected-media"
              onError={(e) => {
                e.currentTarget.src = 'https://images.pexels.com/photos/1183992/pexels-photo-1183992.jpeg?auto=compress&cs=tinysrgb&w=400';
              }}
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              draggable={false}
            />
            <div className="watermark-overlay"></div>
            <div className="security-overlay"></div>
          </div>
        </div>
      );
    }
    
    // Video
    if (currentMedia.type === 'video') {
      return <VideoPlayer src={currentMedia.url} title={post.title} />;
    }
    
    // Audio
    if (currentMedia.type === 'audio') {
      return <AudioPlayer src={currentMedia.url} title={post.title} artist={post.author?.full_name || ''} />;
    }
    
    // Documents (PDF, EPUB)
    if (currentMedia.type === 'document') {
      const isEbook = currentMedia.file_name.toLowerCase().includes('.epub') || 
                     currentMedia.file_name.toLowerCase().includes('.pdf');
      
      if (isEbook) {
        return <EbookViewer src={currentMedia.url} title={post.title} type={currentMedia.file_name.toLowerCase().includes('.epub') ? 'epub' : 'pdf'} />;
      }
      
      // Other documents - open in new tab
      return (
        <div className="w-full h-full flex items-center justify-center relative">
          {renderFallbackCard()}
          <button
            onClick={() => window.open(currentMedia.url, '_blank')}
            className="absolute bottom-6 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-lg"
          >
            {openDocumentLabel}
          </button>
        </div>
      );
    }
    
    // 3D Models
    if (currentMedia.type === '3d') {
      return <ThreeDViewer src={currentMedia.url} title={post.title} />;
    }
    
    // Fallback
    return (
      <div className="w-full h-full flex items-center justify-center relative">
        {renderFallbackCard()}
        <button
          onClick={() => window.open(currentMedia.url, '_blank')}
          className="absolute bottom-6 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-lg"
        >
          {downloadFileLabel}
        </button>
      </div>
    );
  };

  return (
    <div className="w-full h-full relative bg-black select-none" style={{ userSelect: 'none' }}>
      {renderMediaViewer()}
      
      {/* Media Navigation */}
      {media.length > 1 && !hasMultipleImages && (
        <>
          <button
            onClick={() => setCurrentMediaIndex(prev => prev > 0 ? prev - 1 : media.length - 1)}
            className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 p-2 md:p-3 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all z-30"
          >
            <ChevronLeft size={20} className="md:w-6 md:h-6" />
          </button>
          
          <button
            onClick={() => setCurrentMediaIndex(prev => prev < media.length - 1 ? prev + 1 : 0)}
            className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 p-2 md:p-3 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all z-30"
          >
            <ChevronRight size={20} className="md:w-6 md:h-6" />
          </button>
          
          {/* Media Indicators */}
          <div className="absolute bottom-2 md:bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-1 md:space-x-2 z-30">
            {media.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentMediaIndex(index)}
                className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full transition-all ${
                  index === currentMediaIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MediaViewer;
